import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { TripStopCardItem } from "hooks/maps/useTripPlannerStops";
import {
  getLocationDisplayTitle,
  getPlaceTypeLabel,
} from "libraries/map";
import { getPlaceTypeMarkerIconUrl } from "libraries/placeTypeIcons";
import { getItineraryMarkerBadge } from "libraries/itineraryMarker";
import {
  getPlaceActivityEmissionKg,
  getPlaceActivityTooltip,
} from "libraries/placeTypeEmissions";
import { toLatLng } from "libraries/flight";
import { hopEmissionKg } from "libraries/legEmissions";
import { TripLegMode } from "types/map";
import TripLegModeToggle from "components/map/TripLegModeToggle";
import { useAppSelector } from "redux/hooks";
import {
  selectReferenceCar,
  selectReferenceElectricGridCountry,
} from "redux/slices/shed/shedSlice";

const HOP_CHIP_TOOLTIP: Partial<Record<TripLegMode, string>> = {
  fly: "Estimated flight emissions for this leg: short-haul ~0.246 kg CO₂/km (ADEME-style), including a 9% path detour.",
  train:
    "Estimated transit emissions for this leg: ~12.8 g CO₂/km (ADEME-style rail average).",
  walk: "CO₂ avoided by walking instead of driving this distance with the selected vehicle (negative = savings).",
  bike: "CO₂ avoided by biking instead of driving this distance with the selected vehicle (negative = savings).",
};

type Props = {
  stop: TripStopCardItem;
  index: number;
  onRemove: (stop: TripStopCardItem) => void;
  draggable?: boolean;
  isDragging?: boolean;
  isDropTarget?: boolean;
  onDragStart?: (index: number) => void;
  onDragOver?: (index: number) => void;
  onDragEnd?: () => void;
  onDrop?: (index: number) => void;
  onLegModeChange?: (incomingLegIndex: number, mode: TripLegMode) => void;
};

const TripStopCard = ({
  stop,
  index,
  onRemove,
  draggable = false,
  isDragging = false,
  isDropTarget = false,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  onLegModeChange,
}: Props) => {
  const referenceCar = useAppSelector(selectReferenceCar);
  const gridCountry = useAppSelector(selectReferenceElectricGridCountry);
  const title = getLocationDisplayTitle(stop.location);
  const address = stop.location.formattedAddress?.trim();
  const typeLabel = getPlaceTypeLabel(stop.location.placeTypes);
  const showAddressSecondary =
    Boolean(address) && address !== title;
  const badge = getItineraryMarkerBadge(stop.role, stop.roleLabel);
  const markerIcon = getPlaceTypeMarkerIconUrl(stop.location.placeTypes);
  const activityKg = getPlaceActivityEmissionKg(stop.location.placeTypes);
  const activityTooltip = getPlaceActivityTooltip(stop.location.placeTypes);
  const canSetLegMode =
    stop.incomingLegIndex !== undefined && Boolean(stop.previousLocation);
  const origin = stop.previousLocation
    ? toLatLng(stop.previousLocation)
    : null;
  const destination = toLatLng(stop.location);
  const incomingMode = stop.incomingLegMode ?? "drive";
  const hopKg =
    origin && destination
      ? hopEmissionKg(incomingMode, origin, destination, {
          car: referenceCar,
          gridCountry,
        })
      : undefined;
  const isAvoidedMode = incomingMode === "walk" || incomingMode === "bike";
  const hopChipLabel =
    hopKg === undefined
      ? null
      : isAvoidedMode
        ? `${hopKg.toFixed(1)} kg CO₂`
        : `~${hopKg.toFixed(1)} kg CO₂`;
  const hopTooltip = HOP_CHIP_TOOLTIP[incomingMode];

  return (
    <Paper
      variant="outlined"
      elevation={0}
      draggable={draggable}
      onDragStart={(event) => {
        if (!draggable) {
          return;
        }
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", String(index));
        onDragStart?.(index);
      }}
      onDragOver={(event) => {
        if (!draggable) {
          return;
        }
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
        onDragOver?.(index);
      }}
      onDrop={(event) => {
        if (!draggable) {
          return;
        }
        event.preventDefault();
        onDrop?.(index);
      }}
      onDragEnd={() => onDragEnd?.()}
      sx={{
        px: 1.25,
        py: 1,
        borderRadius: 1,
        bgcolor: isDropTarget ? "action.selected" : "background.paper",
        opacity: isDragging ? 0.55 : 1,
        cursor: draggable ? "grab" : "default",
        borderColor: isDropTarget ? "primary.main" : undefined,
        transition: "background-color 120ms ease, opacity 120ms ease",
        "&:active": draggable ? { cursor: "grabbing" } : undefined,
      }}
    >
      <Stack gap={0.75}>
        <Stack direction="row" alignItems="flex-start" gap={0.75}>
          {draggable && (
            <Box
              aria-hidden
              sx={{
                mt: 0.75,
                color: "text.disabled",
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <DragIndicatorIcon fontSize="small" />
            </Box>
          )}
          <Box
            position="relative"
            flexShrink={0}
            width={40}
            height={48}
            sx={{ mt: 0.1 }}
          >
            <Box
              component="img"
              src={markerIcon}
              alt={`${stop.roleLabel} marker`}
              sx={{
                width: 40,
                height: 48,
                display: "block",
                filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.2))",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: -2,
                right: -4,
                minWidth: 16,
                height: 16,
                px: 0.35,
                borderRadius: 8,
                bgcolor: "primary.main",
                color: "common.white",
                fontSize: 10,
                fontWeight: 700,
                lineHeight: "16px",
                textAlign: "center",
                boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
              }}
            >
              {badge}
            </Box>
          </Box>
          <Box flex={1} minWidth={0}>
            <Stack
              direction="row"
              alignItems="center"
              gap={0.75}
              flexWrap="wrap"
              useFlexGap
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  fontWeight: 600,
                  lineHeight: 1.2,
                }}
              >
                {stop.roleLabel}
                {typeLabel ? ` · ${typeLabel}` : ""}
              </Typography>
              {activityKg !== undefined && activityTooltip && (
                <Tooltip title={activityTooltip} placement="top">
                  <Chip
                    size="small"
                    label={`~${activityKg.toFixed(1)} kg CO₂`}
                    sx={{
                      height: 20,
                      borderRadius: 10,
                      bgcolor: "success.main",
                      color: "common.white",
                      fontWeight: 700,
                      fontSize: "0.65rem",
                      "& .MuiChip-label": {
                        px: 0.9,
                        py: 0,
                      },
                    }}
                  />
                </Tooltip>
              )}
              {hopKg !== undefined &&
                incomingMode !== "drive" &&
                hopChipLabel &&
                hopTooltip && (
                  <Tooltip title={hopTooltip} placement="top">
                    <Chip
                      size="small"
                      label={hopChipLabel}
                      sx={{
                        height: 20,
                        borderRadius: 10,
                        bgcolor: "success.main",
                        color: "common.white",
                        fontWeight: 700,
                        fontSize: "0.65rem",
                        "& .MuiChip-label": {
                          px: 0.9,
                          py: 0,
                        },
                      }}
                    />
                  </Tooltip>
                )}
            </Stack>
            <Typography
              variant="body2"
              color="text.primary"
              sx={{
                mt: 0.25,
                lineHeight: 1.35,
                wordBreak: "break-word",
                fontWeight: 500,
              }}
            >
              {title}
            </Typography>
            {showAddressSecondary && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  mt: 0.15,
                  lineHeight: 1.3,
                  wordBreak: "break-word",
                }}
              >
                {address}
              </Typography>
            )}
          </Box>
          <IconButton
            aria-label={`Remove ${stop.roleLabel}`}
            size="small"
            onClick={() => onRemove(stop)}
            onMouseDown={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
            sx={{ mt: -0.25, flexShrink: 0 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        {canSetLegMode && (
          <TripLegModeToggle
            mode={stop.incomingLegMode ?? "drive"}
            from={origin}
            to={destination}
            car={referenceCar}
            gridCountry={gridCountry}
            onChange={(mode) =>
              onLegModeChange?.(stop.incomingLegIndex as number, mode)
            }
          />
        )}
      </Stack>
    </Paper>
  );
};

export default TripStopCard;
